import { OrderQuoteStatus, useGetClientAllOrderQuoteQuery } from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { FileText, MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { StatusBadge } from '../../components/ui/status-badge';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { SearchInput } from '../../components/ui/search-input';
import { formatDate } from '../../lib/utils';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { CreateQuoteModal } from '../../components/quotes/CreateQuoteModal';

/* ── Status tone mapping ── */

const STATUS_CONFIG: Record<
	string,
	{ label: string; tone: 'success' | 'warn' | 'info' | 'neutral' | 'danger' }
> = {
	[OrderQuoteStatus.Draft]: { label: 'Draft', tone: 'neutral' },
	[OrderQuoteStatus.Requested]: { label: 'Requested', tone: 'info' },
	[OrderQuoteStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderQuoteStatus.Submitted]: { label: 'Submitted', tone: 'info' },
	[OrderQuoteStatus.Accepted]: { label: 'Accepted', tone: 'success' },
	[OrderQuoteStatus.Rejected]: { label: 'Rejected', tone: 'danger' },
	[OrderQuoteStatus.Expired]: { label: 'Expired', tone: 'neutral' },
};

function QuoteStatusBadge({ status }: { status: string }) {
	const config = STATUS_CONFIG[status] ?? { label: status, tone: 'neutral' as const };
	return <StatusBadge active={true} activeLabel={config.label} activeTone={config.tone} />;
}

/* ── Empty state ── */

function EmptyState({ search }: { search: string }) {
	return (
		<Card className="border-line-1 bg-surface py-16">
			<div className="flex flex-col items-center gap-3 text-center">
				<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
					<FileText size={20} />
				</div>
				<div className="text-base text-text-2">No quotes found</div>
				<div className="text-sm text-text-4">
					{search
						? 'Try a different search term.'
						: 'Create your first quote to get started.'}
				</div>
			</div>
		</Card>
	);
}

/* ── Loading skeleton ── */

function TableSkeleton() {
	return (
		<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-4 px-4 py-3.5"
					style={{ borderBottom: i < 5 ? '0.5px solid var(--line-1)' : 'none' }}
				>
					<div className="h-4 w-40 animate-pulse rounded bg-surface-fill" />
					<div className="h-5 w-16 animate-pulse rounded bg-surface-fill" />
					<div className="ml-auto h-3 w-20 animate-pulse rounded bg-surface-fill" />
				</div>
			))}
		</Card>
	);
}

/**
 * One quote row, for sizing scroll-mode batches: TableCell's 10px vertical padding around the
 * name (13px × 1.5) over its one-line description (2px gap + 11px × 1.5), plus the 1px border.
 */
function quoteRows(): RowEstimate {
	return { rowHeight: 59 };
}

/* ════════════════════════ QUOTES PAGE ════════════════════════ */

export function QuotesPage() {
	const navigate = useNavigate();
	const {
		search,
		setSearch,
		debouncedSearch,
		setPage,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		mode,
		setMode,
		request,
		loadMore,
		reload,
		limit,
	} = useListParams({ defaultSort: 'createdAt', defaultDir: 'desc', scrollRows: quoteRows });
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [createOpen, setCreateOpen] = useState(false);

	const statusFilter = tab === 'all' ? undefined : [tab as OrderQuoteStatus];

	const { data, loading, refetch } = useGetClientAllOrderQuoteQuery({
		variables: {
			input: {
				pagination: request,
				search: debouncedSearch || undefined,
				order: { field: sortField, direction: sortDirection },
				statuses: statusFilter,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getClientAllOrderQuote,
		loading
	);
	const quotes = list.items;
	const total = list.pageInfo?.totalCount ?? 0;

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Quotes
					</h1>
					<p className="text-base text-text-3">
						View and manage your quotes — {total} total.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						New quote
					</Button>
				</div>
			</div>

			{/* ── Tabs + search ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All quotes' },
						{ value: OrderQuoteStatus.Draft, label: 'Drafts' },
						{ value: OrderQuoteStatus.Requested, label: 'Requested' },
						{ value: OrderQuoteStatus.Submitted, label: 'Submitted' },
						{ value: OrderQuoteStatus.Pending, label: 'Pending' },
						{ value: OrderQuoteStatus.Accepted, label: 'Accepted' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search quotes…"
						className="flex-1 lg:flex-none lg:w-[220px]"
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Table ── */}
			{list.loading ? (
				<TableSkeleton />
			) : quotes.length === 0 ? (
				<EmptyState search={search} />
			) : (
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Quote
									</SortableTableHead>
									<SortableTableHead
										sortKey="status"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[120px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Status
									</SortableTableHead>
									<TableHead className="w-[100px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Purpose
									</TableHead>
									<SortableTableHead
										sortKey="deadline"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[120px] text-right text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Deadline
									</SortableTableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[110px] text-right text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{quotes.map((q) => (
									<TableRow
										key={q.id}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() => navigate(`/quotes/${q.id}`)}
									>
										<TableCell>
											<div className="text-base font-medium text-text-1">
												{q.name}
											</div>
											{q.description && (
												<div className="mt-0.5 line-clamp-1 text-xs text-text-4">
													{q.description}
												</div>
											)}
										</TableCell>
										<TableCell>
											<QuoteStatusBadge status={q.status} />
										</TableCell>
										<TableCell className="text-base capitalize text-text-3">
											{q.purpose?.toLowerCase().replace(/_/g, ' ') || '—'}
										</TableCell>
										<TableCell className="text-right text-base text-text-3">
											{q.deadline ? formatDate(q.deadline) : '—'}
										</TableCell>
										<TableCell className="text-right text-base text-text-3">
											{q.createdAt ? formatDate(q.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																navigate(`/quotes/${q.id}`);
															}}
														>
															View details
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<ListFooter list={list} />

			<CreateQuoteModal
				open={createOpen}
				onOpenChange={setCreateOpen}
				onSuccess={() => reload(refetch)}
			/>
		</div>
	);
}
